import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import cn from 'classnames';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
var POSITIONS = ['top', 'right', 'bottom', 'left'];
var ARROW_SIZE = 10;
var ARROW_MARGIN = ARROW_SIZE + 6;

// Because of the following bug in React we had to "fake" disabled styles on disabled children
// the onMouseLeave event is not triggered if the the container contains disabled elements
// https://github.com/facebook/react/issues/4251#issuecomment-167833472
// https://github.com/facebook/react/issues/10109

var getStyles = function getStyles(tooltipTriggerRef, tooltipRef, position) {
  if (!tooltipTriggerRef.current || !tooltipRef.current) {
    return {};
  }
  var triggerRect = tooltipTriggerRef.current.getBoundingClientRect();
  var tooltipRect = tooltipRef.current.getBoundingClientRect();
  var posStyles = {};
  if (position === 'top') {
    posStyles = _objectSpread({
      top: triggerRect.top - tooltipRect.height - ARROW_MARGIN,
      left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
    }, styles.arrow[position]);
  }
  if (position === 'right') {
    posStyles = _objectSpread({
      left: triggerRect.right + ARROW_MARGIN,
      top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
    }, styles.arrow[position]);
  }
  if (position === 'bottom') {
    posStyles = _objectSpread({
      top: triggerRect.bottom + ARROW_MARGIN,
      left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
    }, styles.arrow[position]);
  }
  if (position === 'left') {
    posStyles = _objectSpread({
      top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.left - tooltipRect.width - ARROW_MARGIN
    }, styles.arrow[position]);
  }
  return posStyles;
};
var Tooltip = function Tooltip(_ref) {
  var _children$props;
  var children = _ref.children,
    className = _ref.className,
    position = _ref.position,
    text = _ref.text,
    usePortal = _ref.usePortal,
    delay = _ref.delay,
    disabled = _ref.disabled,
    sxOverrides = _ref.sxOverrides;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    show = _useState2[0],
    setShow = _useState2[1];
  var tooltipTriggerRef = useRef(null);
  var tooltipRef = useRef(null);
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    tooltipTimeout = _useState4[0],
    setTootipTimeout = _useState4[1]; // in case there are no children provided, do not render anything
  // we have to handle this case extra because getBoundingClientRect will cause an error when called on null
  if (!children) {
    return null;
  }
  if (disabled) {
    return children;
  }
  var getTooltipContent = function getTooltipContent() {
    return jsx("div", {
      id: "tooltip",
      ref: tooltipRef,
      className: cn('zenit', className),
      sx: _objectSpread(_objectSpread(_objectSpread({}, styles.tooltip), getStyles(tooltipTriggerRef, tooltipRef, position)), {}, {
        visibility: show ? 'visible' : 'hidden'
      })
    }, text);
  };
  var showTooltip = function showTooltip() {
    setTootipTimeout(setTimeout(function () {
      return setShow(true);
    }, delay));
  };
  var hideTooltip = function hideTooltip() {
    clearTimeout(tooltipTimeout);
    setTootipTimeout(0);
    setShow(false);
  };
  return jsx("div", {
    className: cn('zenit', className),
    sx: _objectSpread({
      display: 'inline-block',
      position: 'relative'
    }, sxOverrides),
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip
  }, jsx("span", {
    id: "tooltip-trigger",
    sx: _objectSpread(_objectSpread({}, styles.trigger), ((_children$props = children.props) === null || _children$props === void 0 ? void 0 : _children$props.disabled) && {
      cursor: 'not-allowed',
      pointerEvents: 'none'
    }),
    ref: tooltipTriggerRef
  }, children), usePortal ? /*#__PURE__*/createPortal(getTooltipContent(), document.body) : getTooltipContent());
};
var baseArrowStyle = {
  content: '""',
  position: 'absolute',
  borderWidth: "".concat(ARROW_SIZE, "px"),
  borderStyle: 'solid',
  borderTopColor: 'transparent',
  borderRightColor: 'transparent',
  borderBottomColor: 'transparent',
  borderLeftColor: 'transparent'
};
var styles = {
  trigger: {
    display: 'block'
  },
  tooltip: {
    maxWidth: '288px',
    padding: 3,
    backgroundColor: 'grey.10',
    color: 'grey.0',
    borderRadius: 1,
    fontSize: 1,
    display: 'block',
    zIndex: 9999,
    // tooltips in modal have to be even higher than modal (9998)
    position: 'fixed'
  },
  arrow: {
    top: {
      '::after': _objectSpread(_objectSpread({}, baseArrowStyle), {}, {
        top: '100%',
        left: '50%',
        marginLeft: "-".concat(ARROW_SIZE, "px"),
        borderTopColor: 'grey.10'
      })
    },
    right: {
      '::after': _objectSpread(_objectSpread({}, baseArrowStyle), {}, {
        top: '50%',
        right: '100%',
        marginTop: "-".concat(ARROW_SIZE, "px"),
        borderRightColor: 'grey.10'
      })
    },
    bottom: {
      '::after': _objectSpread(_objectSpread({}, baseArrowStyle), {}, {
        bottom: '100%',
        left: '50%',
        marginLeft: "-".concat(ARROW_SIZE, "px"),
        borderBottomColor: 'grey.10'
      })
    },
    left: {
      '::after': _objectSpread(_objectSpread({}, baseArrowStyle), {}, {
        top: '50%',
        left: '100%',
        marginTop: "-".concat(ARROW_SIZE, "px"),
        borderLeftColor: 'grey.10'
      })
    }
  }
};
Tooltip.defaultProps = {
  position: POSITIONS[0],
  usePortal: true,
  delay: 0,
  disabled: false,
  sxOverrides: {}
};
export { Tooltip };